import type OriginTracing from '../originTracing';
import type { Logger } from '../resiliencedb/types';
import getBatchableQueue, {
	type BatchableQueue,
	type BatchFlushCallback,
	type RetryQueueOptions,
} from '../resilienceQueue';
import getMetricsCollector from '../resilienceQueue/Metrics';
import type { MetricsCollector } from '../resilienceQueue/MetricsCollector';
import { type Context, type EventOverrides, type TenantInfo, type UserInfo } from '../types';
import uuid from '../uuid';
import { attachXidToMultipleEvents } from '../xid/attachXidToMultipleEvents';
import type { XIDPromise } from '../xid/XIDPromise';

import { AnalyticsResponse } from './AnalyticsResponse';
import { buildContext } from './buildContext';
import { DEFAULT_REQUEST_TIMEOUT } from './defaults';
import { EventResponse } from './EventResponse';
import { type EventValidationResult } from './EventValidationResult';
import { FetchError } from './FetchError';
import { HTTP_STATUS_CODE_MAP } from './HttpStatusCode';
import { LastError } from './LastError';
import { type IMetadataClient } from './metadataClient';
import type { MetadataResponse } from './MetadataResponse';
import { prepareEventContext } from './prepareEventContext';
import { prepareEventProperties } from './prepareEventProperties';
import { sendEvents } from './sendEvents';
import {
	ALLOW_LISTED_FETCH_ERROR_NAMES,
	type BaseSegmentEvent,
	type LibraryMetadataDef,
	type PackagedEvent,
	type SegmentBatchDef,
	type SegmentEvent,
	SegmentEventTypes,
	type SegmentIdentifyEventDef,
	type SegmentIdentifyEventTraitsDef,
	type SegmentProperties,
	type SegmentScreenEventDef,
	type SegmentTrackEventDef,
	type SegmentTrackPropertiesDef,
} from './types';
import User from './user';

export type Options = {
	apiHost: string;
	apiHostProtocol: string;
	disableCookiePersistence?: boolean;
	enableEventCalls?: boolean;
	enableMetadataCalls?: boolean;
	env: string;
	logger?: Logger;
	metadataClient: IMetadataClient;
	perimeter?: string;
	product: string;
	requestTimeout?: number;
	responseCallback: ResponseCallback<AnalyticsResponse>;
	retryQueueOptions?: RetryQueueOptions;
	retryQueuePrefix: string;
	xidPromiseGetter: () => ReturnType<typeof XIDPromise>;
};

class AnalyticsAPIResponse {
	success: boolean;
	validationErrors: Map<string, string>;
	message: Message;
	code: number;
	validationReports?: EventValidationReport[] | null | undefined;

	constructor(
		success: boolean,
		validationErrors: Map<string, string>,
		message: Message,
		code: number,
		validationReports?: EventValidationReport[] | null | undefined,
	) {
		this.success = success;
		this.validationErrors = validationErrors;
		this.message = message;
		this.code = code;
		this.validationReports = validationReports;
	}

	public isSuccess(): boolean {
		return this.success;
	}

	public getValidationErrors(): Map<string, string> {
		return this.validationErrors;
	}

	public getMessage(): Message {
		return this.message;
	}

	public getCode(): number {
		return this.code;
	}

	public getValidationReports(): EventValidationReport[] | null {
		return this.validationReports === undefined ? null : this.validationReports;
	}
}

class Message {
	error: string;

	constructor(error: string) {
		this.error = error;
	}
}

class EventValidationReport {
	messageId: string;
	results: EventValidationResult[];

	constructor(messageId: string, results: EventValidationResult[]) {
		this.messageId = messageId;
		this.results = results;
	}

	public getMessageId(): string {
		return this.messageId;
	}

	public getResults(): EventValidationResult[] {
		return this.results;
	}
}

export type ResponseCallback<AnalyticsResponse> = (response: AnalyticsResponse) => void;

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export default class EventProcessor {
	private user: User;
	private options: Required<Options>;

	private resilienceQueue: BatchableQueue<PackagedEvent>;

	private gasv3BatchUrl: string;
	private metrics: MetricsCollector;
	private xidPromiseCallback: ReturnType<typeof XIDPromise>;

	private responseCallback: ResponseCallback<AnalyticsResponse>;

	private metadataClient: IMetadataClient;
	private useUrlFromMetadata: boolean;
	private lastSendEventsError: LastError | null;
	private lastUrlFallbackError: LastError | null;
	private originTracingClient: OriginTracing | null = null;

	constructor(options: Options) {
		this.options = {
			...options,
			requestTimeout: options.requestTimeout || DEFAULT_REQUEST_TIMEOUT,
			retryQueueOptions: options.retryQueueOptions || {},
			logger: options.logger || console,
			disableCookiePersistence: options.disableCookiePersistence || false,
			env: options.env,
			perimeter: options.perimeter || '',
			enableMetadataCalls:
				typeof options.enableMetadataCalls === 'boolean' ? options.enableMetadataCalls : true,
			enableEventCalls:
				typeof options.enableEventCalls === 'boolean' ? options.enableEventCalls : true,
		};
		this.user = new User(this.options?.disableCookiePersistence);

		this.xidPromiseCallback = options.xidPromiseGetter();
		this.gasv3BatchUrl = `${options.apiHostProtocol}://${options.apiHost}/batch`;
		this.metrics = getMetricsCollector();

		this.responseCallback = options.responseCallback;

		this.resilienceQueue = getBatchableQueue(
			options.retryQueuePrefix,
			options.product,
			this.options.retryQueueOptions,
			this.options.logger,
		);

		this.metadataClient = options.metadataClient;
		this.useUrlFromMetadata = true;
		this.lastSendEventsError = null;
		this.lastUrlFallbackError = null;

		setInterval(
			() => {
				this.useUrlFromMetadata = true;
			},
			10 * 60 * 1000,
		);

		this.resilienceQueue.start(this.sendEvents);
	}

	getUser(): User {
		return this.user;
	}

	async track(
		eventName: string,
		builtEvent: SegmentTrackPropertiesDef & EventOverrides,
		context: Context,
		callback?: () => void,
	): Promise<void> {
		const baseEvent = this.buildBaseEvent(context, SegmentEventTypes.TRACK, builtEvent);
		const eventWithoutMessageId: Omit<SegmentTrackEventDef, 'messageId'> = {
			...baseEvent,
			type: SegmentEventTypes.TRACK,
			properties: prepareEventProperties(builtEvent),
			event: eventName,
		};
		const event: SegmentTrackEventDef = {
			...eventWithoutMessageId,
			messageId: this.createMessageId(),
		};
		const packagedEvent = this.packageEvent(event);
		await this.enqueueEvent(packagedEvent);
		if (callback) {
			callback();
		}
	}

	async page(
		eventName: string,
		builtEvent: SegmentProperties & EventOverrides,
		context: Context,
		callback?: () => void,
	): Promise<void> {
		const baseEvent = this.buildBaseEvent(context, SegmentEventTypes.PAGE, builtEvent);
		const eventWithoutMessageId: Omit<SegmentScreenEventDef, 'messageId'> = {
			...baseEvent,
			type: SegmentEventTypes.PAGE,
			properties: prepareEventProperties(builtEvent),
			name: eventName,
		};
		const event: SegmentScreenEventDef = {
			...eventWithoutMessageId,
			messageId: this.createMessageId(),
		};
		const packagedEvent = this.packageEvent(event);
		await this.enqueueEvent(packagedEvent);
		if (callback) {
			callback();
		}
	}

	// Segment uses the identifier to update user id which we have already done in the analyticsWebClient.ts
	async identify(
		_identifier: string,
		builtEvent: SegmentIdentifyEventTraitsDef & EventOverrides,
		context: Context,
		callback?: () => void,
	): Promise<void> {
		const baseEvent = this.buildBaseEvent(context, SegmentEventTypes.IDENTIFY, builtEvent);
		const eventWithoutMessageId: Omit<SegmentIdentifyEventDef, 'messageId'> = {
			...baseEvent,
			type: SegmentEventTypes.IDENTIFY,
			traits: prepareEventProperties(builtEvent),
		};
		const event: SegmentIdentifyEventDef = {
			...eventWithoutMessageId,
			messageId: this.createMessageId(),
		};
		const packagedEvent = this.packageEvent(event);
		await this.enqueueEvent(packagedEvent);
		if (callback) {
			callback();
		}
	}

	private async enqueueEvent(packagedEvent: any): Promise<void> {
		if (this.options.enableMetadataCalls) {
			this.metadataClient.start(false); // Start the metadata client if needed
		}
		await this.resilienceQueue.addItem(packagedEvent);
	}

	private buildBaseEvent(
		context: Context,
		type: SegmentEventTypes,
		overrides: EventOverrides,
	): Omit<BaseSegmentEvent, 'messageId'> {
		const clonedContext = prepareEventContext(context);
		const segmentContext = buildContext(clonedContext, overrides);
		return {
			context: segmentContext,
			timestamp: new Date().toISOString(),
			type,
			userId: this.user.getUserId(),
			anonymousId: overrides.anonymousId || this.user.getAnonymousId(),
		};
	}

	private createMessageId(): string {
		return `ajs-${uuid()}`;
	}

	private packageEvent(event: SegmentEvent): PackagedEvent {
		const { apiHost, apiHostProtocol } = this.options;
		return {
			headers: {
				'Content-Type': 'text/plain',
			},
			msg: event,
			url: `${apiHostProtocol}://${apiHost}/${event.type.charAt(0)}`,
		};
	}

	// Using anonymous function so it can have the BatchFlushCallback type associated with it
	// And to allow it to not need bind when passing through to ResilieceQueue.
	public sendEvents: BatchFlushCallback<PackagedEvent> = async (items, callback) => {
		if (this.options.enableEventCalls === false) {
			return;
		}

		const httpRetryCount = this.resilienceQueue.getGlobalRetryCount();
		const metricsPayload = this.metrics.getMetricsPayload();

		const eventsWithXID = await this.attachXIDs(items);

		// Calculating sentAt after the XID generation as this may take some time.
		const sentAt = new Date().toISOString();

		const metadataInfo = await this.metadataClient.getMetadataAsync();

		const [eventUrl, isFallbackUrl] = this.getEventUrl(metadataInfo);

		const metadata: LibraryMetadataDef = {
			...metricsPayload,
			httpRetryCount,
			lastSendEventsErrorReason: this.lastSendEventsError?.message || null, // Temporary back-compat
			lastSendEventsError: this.lastSendEventsError,
			lastUrlFallbackError: this.lastUrlFallbackError,
			isUsingFallbackUrl: isFallbackUrl,
			props: metadataInfo?.props || null,
			metadataClientMetrics: this.metadataClient?.metrics || null,
		};

		// Remove default values from metadata to save space
		this.cleanLibraryMetadata(metadata);

		const unpackagedEvents = eventsWithXID.map((item) => {
			this.applyExclusions(item);
			item.msg.sentAt = sentAt;
			return item.msg;
		});

		const batchBody: SegmentBatchDef = {
			batch: unpackagedEvents,
			sentAt,
			metadata,
		};

		try {
			const response = await sendEvents({
				url: eventUrl,
				batch: batchBody,
				timeout: this.options.requestTimeout,
			});

			this.lastSendEventsError = null;
			this.lastUrlFallbackError = null;

			this.metrics.subtractFromMetrics(metricsPayload);
			callback(null, response);
			this.originTracingClient?.cleanupOriginParameters(items);
			await this.invokeLocalResponseCallbackAsync(items, response, null);
		} catch (error) {
			// If there's an error, other than one which indicates a temporary problem with the service,
			// we'll stop using the URL from the metadata and fall back to the default URL. After a time
			// we'll fall forward again in case the problem has been resolved.
			if (error instanceof FetchError && [429, 503].includes(error.statusCode)) {
				// Ignore 429 and 503 errors since they're considered to indicate a temporary
				// issue with the service, rather than a network failure due to blocked requests.
				this.lastSendEventsError = new LastError('ServerBusyError', error);
			} else {
				const errorName =
					error instanceof Error && ALLOW_LISTED_FETCH_ERROR_NAMES.includes(error.name)
						? error.name
						: 'Unknown';

				this.lastSendEventsError = new LastError(errorName, error);

				// Handle falling back to the default URL if we're using the metadata URL.
				this.useUrlFromMetadata = false;

				// If we're now using the fallback URL, and the last attempt wasn't,
				// we'll record this as the error that triggered the fallback.
				const [_, willUseFallbackUrl] = this.getEventUrl(metadataInfo);
				if (willUseFallbackUrl && willUseFallbackUrl !== isFallbackUrl) {
					this.lastUrlFallbackError = this.lastSendEventsError;
				}
			}

			callback(error, null);
			await this.invokeLocalResponseCallbackAsync(items, null, error);
		}
	};

	private applyExclusions(item: PackagedEvent): void {
		const msg = item.msg;
		const properties = (msg as SegmentTrackEventDef | SegmentScreenEventDef).properties;
		const traits = (msg as SegmentIdentifyEventDef).traits;
		const excluded = msg.context?.excludedProperties;
		if (!excluded) {
			return;
		}

		if (excluded.anonymousId === true) {
			msg.anonymousId = undefined;
		}
		if (excluded.interactionSession === true) {
			if (properties) {
				(properties as any).interactionSession = undefined;
			}
		}
		if (excluded.sessionId === true) {
			if (properties) {
				properties.sessionId = undefined;
			}
		}
		if (excluded.pageLoadId === true) {
			if (properties) {
				properties.pageLoadId = undefined;
			}
		}
		if (excluded.tabId === true) {
			if (properties) {
				properties.tabId = undefined;
			}
		}
		if (excluded.taskSessions === true) {
			if (properties) {
				properties.taskSessions = undefined;
			}
		}
		if (excluded.userId === true) {
			msg.userId = undefined;
			if (properties) {
				properties.userIdType = undefined;
			}
		}
		if (excluded.xid === true) {
			if (properties) {
				properties.xid = undefined;
			}
			if (traits) {
				traits.xid = undefined;
			}
		}
	}

	private getEventUrl(
		metadataInfo: MetadataResponse | null,
	): [url: string, isFallbackUrl: boolean | null] {
		const preferredBatchUrl = metadataInfo?.url ? metadataInfo.url : this.gasv3BatchUrl;
		const finalBatchUrl = this.useUrlFromMetadata ? preferredBatchUrl : this.gasv3BatchUrl;
		const isFallbackUrl =
			preferredBatchUrl === this.gasv3BatchUrl ? null : finalBatchUrl !== preferredBatchUrl;
		return [finalBatchUrl, isFallbackUrl];
	}

	private cleanLibraryMetadata(obj: Record<string, any>): void {
		// skip if the parameter is not an object
		if (obj === null || typeof obj !== 'object') {
			return;
		}

		for (const key in obj) {
			// Skip cleaning up props object and its keys
			// to avoid having to check the existence of the fields in props object
			// and manipulate them to calculate metadata signature in analytics service
			if (key === 'props') {
				continue;
			}
			let value = obj[key];
			// Remove null or undefined values
			if (value === null || value === undefined) {
				delete obj[key];
			}
			// Remove zero-valued numbers
			else if (typeof value === 'number' && value === 0) {
				delete obj[key];
			}
			// Remove empty arrays
			else if (Array.isArray(value)) {
				if (value.length === 0) {
					delete obj[key];
				} else {
					value.forEach((item: any) => {
						this.cleanLibraryMetadata(item);
					});
				}
			}
			// Recurse into objects
			else if (typeof value === 'object') {
				this.cleanLibraryMetadata(obj[key]);
			}
		}
	}

	private async invokeLocalResponseCallbackAsync(
		items: PackagedEvent[] | null,
		response: Response | null,
		error: any | null,
	): Promise<void> {
		const localResponseCallback = this.responseCallback;
		if (localResponseCallback) {
			// If we have a response, we'll parse it and create an AnalyticsAPIResponse from it.
			let analyticsAPIResponse: AnalyticsAPIResponse | null = null;
			if (response) {
				let responseObject: any;

				try {
					responseObject = await response.clone().json();
				} catch {
					// Ignore errors parsing the response, we can't do anything about them
				}

				// Ensure we've got a valid object to work with, filling in any missing
				// properties as best we can.
				responseObject = responseObject || {};
				responseObject.code =
					Number.parseInt(responseObject.code) > 0
						? Number.parseInt(responseObject.code)
						: response.status;
				responseObject.success =
					responseObject.success || (responseObject.code >= 200 && responseObject.code <= 299);

				analyticsAPIResponse = new AnalyticsAPIResponse(
					responseObject.success,
					responseObject.validationErrors || new Map(),
					new Message(responseObject.message?.error || ''),
					responseObject.code,
					responseObject.validationReports || null,
				);
			}

			// If we have an error, but it's not an instance of Error (e.g. it's a string), we'll convert it to an Error object.
			if (error && !(error instanceof Error)) {
				error = new Error(String(error));
			}

			const analyticsResponse: AnalyticsResponse = BatchInterceptor.toAnalyticsResponse(
				items || [],
				error || null, // In case 'error' were to be undefined we'll convert it to null.
				analyticsAPIResponse,
			);

			localResponseCallback(analyticsResponse);
		}
	}

	private async attachXIDs(items: PackagedEvent[]): Promise<PackagedEvent[]> {
		if (this.xidPromiseCallback) {
			return attachXidToMultipleEvents(items, this.xidPromiseCallback);
		}
		return Promise.resolve(items);
	}

	/** @internal Used by AnalyticsWebClient to wire up origin tracing cleanup. */
	public setOriginTracingClient(client: OriginTracing): void {
		this.originTracingClient = client;
	}

	public setResponseCallback(responseCallback: ResponseCallback<AnalyticsResponse>): void {
		this.responseCallback = responseCallback;
	}

	public refreshMetadata(userInfo: UserInfo | null, tenantInfo: TenantInfo | null): void {
		this.metadataClient.userInfo = userInfo;
		this.metadataClient.tenantInfo = tenantInfo;
	}
}

class BatchInterceptor {
	private static readonly http_error_regex = /HTTP Error\s([0-9]{3})\s\((.+)\)/;
	public static toAnalyticsResponse(
		items: PackagedEvent[],
		error: Error | null,
		analyticsAPIResponse: AnalyticsAPIResponse | null,
	): AnalyticsResponse {
		const eventResponseList: EventResponse[] = [];
		if (analyticsAPIResponse !== null) {
			const [statusCode, success] = [analyticsAPIResponse.code, analyticsAPIResponse.success];
			if (statusCode >= 200 && statusCode <= 299) {
				analyticsAPIResponse.validationReports?.forEach((report: EventValidationReport) => {
					const matchedEvent = items.find(
						(item: PackagedEvent) => item.msg.messageId === report.messageId,
					);
					if (matchedEvent) {
						eventResponseList.push(
							new EventResponse(matchedEvent.msg.messageId, report.results, matchedEvent.msg),
						);
					}
				});
				return new AnalyticsResponse(
					success,
					statusCode,
					HTTP_STATUS_CODE_MAP.get(statusCode) as string,
					eventResponseList,
				);
			} else if (statusCode >= 400 && statusCode <= 499) {
				// not exluding "HTTP 429 Too Many Requests error" as it is thrown as an error and handled below
				const errorMessage =
					analyticsAPIResponse.message?.error !== null
						? analyticsAPIResponse.message.error
						: HTTP_STATUS_CODE_MAP.has(statusCode)
							? HTTP_STATUS_CODE_MAP.get(statusCode)
							: HTTP_STATUS_CODE_MAP.get(-1);
				items.forEach((item: PackagedEvent) => {
					eventResponseList.push(new EventResponse(item.msg.messageId, [], item.msg));
				});
				return new AnalyticsResponse(
					success,
					statusCode,
					errorMessage as string,
					eventResponseList,
				);
			}
		} else if (error?.message) {
			const match = error.message.match(BatchInterceptor.http_error_regex);
			if (match) {
				items.forEach((item: PackagedEvent) => {
					eventResponseList.push(new EventResponse(item.msg.messageId, [], item.msg));
				});
				return new AnalyticsResponse(false, Number(match[1]), match[2], eventResponseList);
			}
		}
		items.forEach((item: PackagedEvent) => {
			eventResponseList.push(new EventResponse(item.msg.messageId, [], item.msg));
		});
		return new AnalyticsResponse(
			false,
			-1,
			HTTP_STATUS_CODE_MAP.get(-1) as string,
			eventResponseList,
		);
	}
}
